<template>
  <div class="size-chart">
    <div
      class="size-chart__fields"
      :class="{ 'double-field': showCategoriesDropdown }"
    >
      <div
        v-if="showCategoriesDropdown"
        class="size-chart__field size-chart__dropdown"
      >
        <h3
          :id="`section-title-${sectionId}`"
          class="size-chart__field-title"
          v-text="categoriesLabel"
        />
        <dropdown-select
          class="size-chart__dropdown-select"
          :options="categoryOptions"
          :predefined-value="defaultCategory"
          @on-option-selected="handleCategoryChange"
        />
      </div>
      <div class="size-chart__field size-chart__dropdown">
        <h3
          class="size-chart__field-title"
          v-text="localesLabel"
        />
        <dropdown-select
          class="size-chart__dropdown-select"
          :options="createOptionsFromString(locales)"
          :predefined-value="defaultLocale"
          @on-option-selected="handleLocaleChange"
        />
      </div>
      <div class="size-chart__field size-chart__radio">
        <radio-group
          class="size-chart__radio-group"
          :options="createOptionsFromString(unitTypes)"
          :predefined-value="defaultUnit"
          @on-option-selected="handleUnitChange"
        />
      </div>
    </div>

    <div
      v-if="tablesToRender.length"
      class="size-chart__tables"
    >
      <h3
        v-if="categoryTitle"
        class="size-chart__field-title size-chart__tables-title"
        v-text="categoryTitle"
      />

      <size-chart-table
        v-for="(table, index) in tablesToRender"
        :key="index"
        class="size-chart__table"
        :table="table"
      />
    </div>
    <div
      v-else-if="state === 'loading'"
      class="size-chart__preloader"
    >
      <div class="spinner-ring">
        <div />
        <div />
        <div />
        <div />
      </div>
    </div>
    <div
      v-else
      class="size-chart__fallback"
    >
      <h3
        class="size-chart__field-title"
        v-text="fallbackText"
      />
    </div>
  </div>
</template>

<script>

/**
 * Component: Size Chart
 * -----------------------------------------------------------------------------
 * - Size Chart component, which can conditionally render categories/fields.
 *
 * @param {String} [context] - Context of the Size Chart. Enables categories depending on the template. Currently accepts either `general` or `product`
 * @param {Array}  [categories] - Categories blocks which contain title and synonyms strings.
 * @param {String} [generalCategory] - General category string to use on size-guide page templates.
 * @param {String} [locales] - A string containing a list of locales separated by a comma.
 * @param {String} [sectionId] - Unique section ID, from Liquid.
 * @param {String} [unitTypes] - A string containing a list of unit types separated by a comma.
 * @param {String} [productType] - The type of current product used to predefine category value.
 */
import { v4 } from 'uuid'

import { mapState } from 'pinia'

import { useLocaleStore } from '~stores/locale'

import { stringToHandle } from '~/helpers/strings'

import DropdownSelect from '~components/dropdown-select/dropdown-select'
import RadioGroup from '~components/radio-group/radio-group'
import SizeChartTable from '~components/size-guide/size-chart-table/size-chart-table'

export default {
  name: 'SizeChart',

  components: {
    DropdownSelect,
    RadioGroup,
    SizeChartTable,
  },

  props: {
    context: {
      type: String,
      default: 'general',
    },
    categories: {
      type: Array,
      default: () => [],
    },
    generalCategory: {
      type: String,
      default: 'General',
    },
    locales: {
      type: String,
      default: 'UK, EU, US',
    },
    sectionId: {
      type: String,
      default: '',
    },
    unitTypes: {
      type: String,
      default: 'cm, in',
    },
    productType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      productCategory: this.productType,
      selectedCategory: stringToHandle(this.generalCategory),
      selectedLocale: '',
      selectedUnitType: '',
      tablesData: {},
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map locale state.
     */
    ...mapState(useLocaleStore, {
      unitSystem: 'unitSystem',
      continentCode: 'continentCode',
      countryCode: 'countryCode',
    }),

    /**
     * Calculates label for `categories` dropdown.
     *
     * @returns {String}
     */
    categoriesLabel() {
      return mint.strings.sizeGuide.categoriesLabel
    },

    /**
     * Calculates label for `locales` dropdown.
     *
     * @returns {String}
     */
    localesLabel() {
      return mint.strings.sizeGuide.localesLabel
    },

    /**
     * Calculates if to show the `categories` dropdown.
     *
     * @returns {Boolean}
     */
    showCategoriesDropdown() {
      return this.context === 'product' && this.categories.length !== 0
    },

    /**
     * Returns a formatted as options `categories` array.
     *
     * @returns {Array}
     */
    categoryOptions() {
      return this.categories.map((category) => ({
        id: v4(),
        label: category.title?.trim(),
        value: stringToHandle(category.title),
      }))
    },

    /**
     * Calculates default category based on the product type.
     *
     * @returns {String}
     */
    defaultCategory() {
      if (!this.showCategoriesDropdown) {
        return stringToHandle(this.generalCategory)
      }

      const matchingCategory = this.categories.find((category) => {
        const formattedProductCategory = this.productCategory.toLowerCase().trim()

        return (
          category.title?.toLowerCase().trim() === formattedProductCategory ||
          category.synonyms?.toLowerCase().includes(formattedProductCategory)
        )
      })

      if (
        this.context !== 'product' ||
        !this.productCategory ||
        !matchingCategory
      ) {
        return stringToHandle(this.categories[0]?.title)
      }

      return stringToHandle(matchingCategory.title)
    },

    /**
     * Calculates title for chosen category.
     *
     * @returns {String}
     */
    categoryTitle() {
      if (!this.showCategoriesDropdown) {
        return ''
      }

      return this.findOptionByValue(this.categoryOptions, this.selectedCategory)?.label
    },

    /**
     * Calculates default unit type based on the measurement system.
     *
     * @returns {String}
     */
    defaultUnit() {
      const unitOptions = this.createOptionsFromString(this.unitTypes)

      switch (this.unitSystem) {
        case 'imperial':
          return unitOptions.find((option) => option.value === 'in')?.value
        case 'metric':
          return unitOptions.find((option) => option.value === 'cm')?.value
        default:
          return unitOptions[0]?.value
      }
    },

    /**
     * Calculates default locale based on the current continent code.
     *
     * @returns {String|Boolean}
     */
    defaultLocale() {
      const localeOptions = this.createOptionsFromString(this.locales)
      let matchingContinent = false

      /**
       * If the continent code is 'NA' or 'US', it selects the correct locale
       * option. This resolves the issue of matching 'NA' to 'US' locale.
       */
      if (
        this.continentCode.toLowerCase() === 'na' ||
        this.continentCode.toLowerCase() === 'us'
      ) {
        matchingContinent = localeOptions.find((option) => {
          return option.value === 'na' || option.value === 'us'
        })?.value
      }

      /**
       * If the country code is `GB` (or `UK)`, the `continentCode` returns `EU`
       * which is not correct. So this resolves the issue of matching 'GB' to 'UK'
       * and choosing the needed locale.
       */
      if (
        this.countryCode.toLowerCase() === 'gb' ||
        this.countryCode.toLowerCase() === 'uk'
      ) {
        matchingContinent = localeOptions.find((option) => {
          return option.value === 'gb' || option.value === 'uk'
        })?.value
      }

      /**
       * - If none of the conditions match, it selects the locale option based on
       * continent code
       * - If no matching locale option is found, it falls back to the first
       * locale option
       */
      if (!matchingContinent) {
        matchingContinent =
          localeOptions.find(
            (option) => option.value === this.continentCode.toLowerCase(),
          )?.value || localeOptions[0]?.value
      }

      return matchingContinent
    },

    /**
     * Calculates the tables which should be visible on the page based on values
     * selected.
     *
     * @returns {array}
     */
    tablesToRender() {
      return (
        this.tablesData[this.selectedCategory]?.tables?.filter((table) => {
          return (
            table.region.toLowerCase() === this.selectedLocale &&
            table.unit_type.toLowerCase() === this.selectedUnitType
          )
        }) || []
      )
    },

    /**
     * Calculates the fallback text in case tables wasn't found/rendered.
     *
     * @returns {String}
     */
    fallbackText() {
      return mint.strings.sizeGuide.fallbackText
    },
  },

  watch: {

    /**
     * Watch when the category changes.
     * - if it already exists in a cached tables object - do nothing
     * - if it's a new category - make a new request for the corresponding
     * metaobject
     */
    selectedCategory(newValue) {
      if (this.tablesData[newValue]) {
        return
      }

      this.fetchMetaTable()
    },
  },

  mounted() {
    this.fetchMetaTable()
  },

  methods: {

    /**
     * Creates a formatted array of options from a given string.
     * @param {string} optionsString - A string containing a list of options separated by a comma.
     *
     * @returns {array}
     */
    createOptionsFromString(optionsString) {
      if (!optionsString) {
        return []
      }

      const optionsArray = optionsString.split(',')

      return optionsArray.map((option) => ({
        id: v4(),
        label: option.trim(),
        value: stringToHandle(option),
      }))
    },

    /**
     * Finds an option in the given array by value.
     * @param {String} value - The value to search for.
     * @param {Array} options - Array of options to search in.
     *
     * @returns {object|boolean} The found option or `false` if not found.
     */
    findOptionByValue(options, value) {
      return options.find((option) => option.value === value) || false
    },

    /**
     * Event handler for category change.
     * @param {string} value - emitted option.value string.
     */
    handleCategoryChange(value) {
      this.selectedCategory = value
    },

    /**
     * Event handler for locale change.
     * @param {string} value - emitted option.value string.
     */
    handleLocaleChange(value) {
      this.selectedLocale = value
    },

    /**
     * Event handler for unit change.
     * @param {string} value - emitted option.value string.
     */
    handleUnitChange(value) {
      this.selectedUnitType = value
    },

    /**
     * Fetches metaobject data for a specific category and updates the component
     * data.
     *
     * @async
     * @throws {Error} Throws an error if the fetch fails.
     */
    async fetchMetaTable() {
      this.state = 'loading'
      try {

        /**
         * Construct the root of the request URL
         * - If a user is browsing from a market subfolder, e.g. `/en-us`, this
         *   value will not have a trailing slash
         * - This adds a slash if the value for the root does not end with one
         */
        let root = mint.routes.root

        if (root.slice(-1) !== '/') {
          root += '/'
        }

        const response = await fetch(
          `${root}pages/size-guide/${this.selectedCategory}`,
        )
        const responseJSON = await response.json()

        this.tablesData[this.selectedCategory] = responseJSON

      } catch (error) {
        mint.Log('error', 'Error fetching size guide metaobject', error, {
          category: this.selectedCategory,
        })
      }

      this.state = 'ready'
    },
  },
}
</script>

<style src="./size-chart.scss" lang="scss"></style>
